import type { BookCardBook } from '@/components/books/book-card';
import { exportMaDocument, updateMaAboutPages, type MaExportDocumentResult, type MaReleaseBookPayload } from '@/lib/ma-books-api';
import { getMaDocument, getTiptapDocumentJson } from '@/lib/zybooks-api';
import convertProseMirrorToZybooksJSON from '@/lib/zybooks-parser/to-zybooks-json';
import { convertProsemirrorTestQuestionsToZybook } from '@/lib/zybooks-parser/to-zybooks-test-question';
import type { JSONContent } from '@tiptap/react';

export interface SyncSectionParams {
    documentName: string;
    devBookName: string;
    zybookCode?: string | null;
    testQuestions?: Record<string, unknown>[];
}

export interface SyncAboutPageParams {
    devBookName: string;
    canonicalSectionId?: number | null;
    sectionNumber?: number | null;
    title?: string | null;
    sectionGuid?: string | null;
    gdocId?: string | null;
    zybookCode?: string | null;
}

/**
 * Converts a section's TipTap document to zyBooks content resources and syncs it to the dev zyBook.
 *
 * @param tiptapJson - The section's ProseMirror/TipTap document.
 * @param params - Target document name and dev zyBook code.
 */
export async function syncSectionToDevBook(tiptapJson: JSONContent, params: SyncSectionParams): Promise<MaExportDocumentResult> {
    const { contentResources } = await convertProseMirrorToZybooksJSON(tiptapJson, {
        zybookCode: params.zybookCode ?? params.devBookName,
    });

    return exportMaDocument({
        documentName: params.documentName,
        devBookName: params.devBookName,
        contentResources,
        testQuestions: params.testQuestions,
    });
}

export async function syncAboutPageToDevBook(tiptapJson: JSONContent, params: SyncAboutPageParams): ReturnType<typeof updateMaAboutPages> {
    const { contentResources } = await convertProseMirrorToZybooksJSON(tiptapJson, {
        zybookCode: params.zybookCode ?? params.devBookName,
        skipGuidGeneration: true,
    });

    return updateMaAboutPages(params.devBookName, {
        aboutPage: {
            canonicalSectionId: params.canonicalSectionId,
            sectionNumber: params.sectionNumber,
            title: params.title,
            sectionGuid: params.sectionGuid,
            gdocId: params.gdocId,
            contentResources,
        },
    });
}

/**
 * Loads a section's document by MA document id and syncs it to the dev zyBook.
 *
 * @param maDocumentId - `MaDocuments.ma_document_id` of the section to sync.
 * @param devBookName - Target dev zyBook code.
 */
export async function syncSectionByDocumentId(maDocumentId: number, devBookName: string): Promise<MaExportDocumentResult> {
    const maDocument = await getMaDocument(maDocumentId);
    const tiptapJson = await getTiptapDocumentJson(maDocument.document_name);

    const placements = maDocument.ma_zybook_documents ?? [];
    const placement = placements.find(row => row.zybook_code === devBookName) ?? placements[0];
    const testQuestionDocId = placement?.ma_test_question_document_ids?.[0];

    let testQuestions: Record<string, unknown>[] | undefined;
    if (testQuestionDocId != null) {
        try {
            const tqMaDocument = await getMaDocument(testQuestionDocId);
            const tqTiptapJson = await getTiptapDocumentJson(tqMaDocument.document_name);
            const converted = await convertProsemirrorTestQuestionsToZybook(tqTiptapJson);
            testQuestions = converted as Record<string, unknown>[];
        } catch {
            // Proceed with section sync if test question document fails to fetch
        }
    }

    return syncSectionToDevBook(
        {
            ...tiptapJson,
            attrs: {
                ...tiptapJson.attrs,
                sectionId: tiptapJson.attrs?.sectionId ?? maDocument.section_guid,
            },
        },
        {
            documentName: maDocument.document_name,
            devBookName,
            testQuestions,
        },
    );
}

export async function syncAboutPageByDocumentId(
    maDocumentId: number,
    devBookName: string,
    params: Omit<SyncAboutPageParams, 'devBookName' | 'sectionGuid' | 'gdocId'> = {},
): ReturnType<typeof updateMaAboutPages> {
    const maDocument = await getMaDocument(maDocumentId);
    const tiptapJson = await getTiptapDocumentJson(maDocument.document_name);

    return syncAboutPageToDevBook(tiptapJson, {
        devBookName,
        ...params,
        sectionGuid: maDocument.section_guid,
        gdocId: maDocument.gdoc_id,
    });
}

/**
 * Prepares book identifiers for release; the server retrieves and converts the documents.
 *
 * @param devBooks - MA dev-book variants included in the release.
 */
export async function prepareReleaseBooks(devBooks: BookCardBook[]): Promise<MaReleaseBookPayload[]> {
    const maBooks = devBooks.filter(book => book.is_ma_zybook);
    if (maBooks.length === 0) {
        throw new Error('This release has no Modern Authoring books');
    }

    return maBooks.map(book => ({
        variant_id: book.variantId ?? 'primary',
        zybook_id: book.zybook_id,
        zybook_code: book.id,
    }));
}
